import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Collapse,
  Tooltip,
  Button,
  LinearProgress,
  Skeleton,
} from '@mui/material';
import {
  ChevronRight,
  ChevronDown,
  Plus,
  CheckCircle,
  Circle,
  Clock,
  AlertCircle,
  Ban,
  PlayCircle,
  GitBranch,
  MoreHorizontal,
} from 'lucide-react';
import dayjs from 'dayjs';

import { Task, TaskStatus, TaskPriority } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';
import { formatPercent } from '@/shared/utils/formatPercentage';

// ============================================================================
// TYPES
// ============================================================================

interface TaskSubtaskTreeProps {
  parentTask: Task;
  allTasks: Task[];
  onTaskClick?: (task: Task) => void;
  onAddSubtask?: (parentTask: Task) => void;
  isLoading?: boolean;
  maxDepth?: number;
}

interface SubtaskNodeProps {
  task: Task;
  allTasks: Task[];
  level: number;
  onTaskClick?: (task: Task) => void;
  onAddSubtask?: (parentTask: Task) => void;
  maxDepth: number;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getStatusIcon = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return <CheckCircle size={14} style={{ color: '#16a34a' }} />;
    case TaskStatus.IN_PROGRESS:
      return <PlayCircle size={14} style={{ color: '#2563eb' }} />;
    case TaskStatus.BLOCKED:
      return <Ban size={14} style={{ color: '#f59e0b' }} />;
    case TaskStatus.CANCELLED:
      return <Ban size={14} style={{ color: '#ef4444' }} />;
    default:
      return <Circle size={14} style={{ color: '#64748b' }} />;
  }
};

const getStatusColor = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return '#16a34a';
    case TaskStatus.IN_PROGRESS:
      return '#2563eb';
    case TaskStatus.BLOCKED:
      return '#f59e0b';
    case TaskStatus.CANCELLED:
      return '#ef4444';
    default:
      return '#64748b';
  }
};

const getStatusLabel = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.NOT_STARTED:
      return 'Not Started';
    case TaskStatus.IN_PROGRESS:
      return 'In Progress';
    case TaskStatus.COMPLETED:
      return 'Completed';
    case TaskStatus.BLOCKED:
      return 'Blocked';
    case TaskStatus.CANCELLED:
      return 'Cancelled';
    default:
      return status;
  }
};

// ============================================================================
// SUBTASK NODE COMPONENT
// ============================================================================

const SubtaskNode: React.FC<SubtaskNodeProps> = ({
  task,
  allTasks,
  level,
  onTaskClick,
  onAddSubtask,
  maxDepth,
}) => {
  const [expanded, setExpanded] = useState(level < 2); // Auto-expand first 2 levels

  // Get subtasks for this task
  const subtasks = allTasks.filter((t) => t.parentTaskId === task.id);
  const hasSubtasks = subtasks.length > 0;
  const canAddMore = level < maxDepth;

  const completedSubtasks = subtasks.filter(
    (t) => t.status === TaskStatus.COMPLETED
  ).length;
  const progressRaw =
    subtasks.length > 0
      ? Math.round((completedSubtasks / subtasks.length) * 100)
      : 0;
  const progress =
    task.status === TaskStatus.COMPLETED ? 100 : progressRaw;

  // Check if overdue
  const isOverdue =
    task.status !== TaskStatus.COMPLETED &&
    task.status !== TaskStatus.CANCELLED &&
    dayjs(task.dueDate).isBefore(dayjs());

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const handleClick = () => {
    onTaskClick?.(task);
  };

  const handleAddSubtask = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddSubtask?.(task);
  };

  return (
    <Box>
      {/* Task Row */}
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          pl: level * 2.5 + 1,
          borderRadius: 1,
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          borderLeft:
            level > 0 ? `2px solid ${getStatusColor(task.status)}20` : 'none',
          '&:hover': {
            bgcolor: 'action.hover',
            '& .subtask-actions': {
              opacity: 1,
            },
          },
        }}
      >
        {/* Expand/Collapse */}
        {hasSubtasks ? (
          <IconButton
            size="small"
            onClick={handleToggle}
            sx={{ p: 0.25, minWidth: 24 }}
          >
            {expanded ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 24 }} />
        )}

        {/* Status Icon */}
        <Tooltip title={getStatusLabel(task.status)}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {getStatusIcon(task.status)}
          </Box>
        </Tooltip>

        {/* Task Title */}
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            fontWeight: level === 0 ? 500 : 400,
            textDecoration:
              task.status === TaskStatus.COMPLETED ? 'line-through' : 'none',
            color:
              task.status === TaskStatus.COMPLETED
                ? 'text.secondary'
                : 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {task.title}
        </Typography>

        {/* Subtask Count Badge */}
        {hasSubtasks && (
          <Tooltip title={`${completedSubtasks}/${subtasks.length} completed`}>
            <Chip
              size="small"
              icon={<GitBranch size={12} />}
              label={`${completedSubtasks}/${subtasks.length}`}
              sx={{
                height: 20,
                fontSize: '0.7rem',
                bgcolor:
                  progress === 100
                    ? 'success.light'
                    : progress > 0
                    ? 'primary.light'
                    : 'grey.200',
                color:
                  progress === 100
                    ? 'success.dark'
                    : progress > 0
                    ? 'primary.dark'
                    : 'text.secondary',
                '& .MuiChip-icon': {
                  color: 'inherit',
                },
              }}
            />
          </Tooltip>
        )}

        {/* Priority */}
        <PriorityBadge priority={task.priority} size="small" />

        {/* Due Date / Overdue */}
        {isOverdue && task.dueDate && dayjs(task.dueDate).isValid() ? (
          <Tooltip title={`Overdue: ${dayjs(task.dueDate).format('MMM D')}`}>
            <AlertCircle size={14} style={{ color: '#ef4444' }} />
          </Tooltip>
        ) : task.dueDate && dayjs(task.dueDate).isValid() ? (
          <Tooltip title={`Due: ${dayjs(task.dueDate).format('MMM D, YYYY')}`}>
            <Typography variant="caption" color="text.secondary">
              {dayjs(task.dueDate).format('MMM D')}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.secondary">—</Typography>
        )}

        {/* Actions */}
        <Box
          className="subtask-actions"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            opacity: 0,
            transition: 'opacity 0.15s ease',
          }}
        >
          {canAddMore && (
            <Tooltip title="Add subtask">
              <IconButton size="small" onClick={handleAddSubtask} sx={{ p: 0.25 }}>
                <Plus size={14} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Subtask Progress Bar (for tasks with subtasks) */}
      {hasSubtasks && expanded && (
        <Box sx={{ pl: level * 2.5 + 4, pr: 2, pb: 0.5 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 3,
              borderRadius: 1,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor: progress === 100 ? 'success.main' : 'primary.main',
              },
            }}
          />
        </Box>
      )}

      {/* Subtasks (Recursive) */}
      <Collapse in={expanded}>
        {subtasks.map((subtask) => (
          <SubtaskNode
            key={subtask.id}
            task={subtask}
            allTasks={allTasks}
            level={level + 1}
            onTaskClick={onTaskClick}
            onAddSubtask={onAddSubtask}
            maxDepth={maxDepth}
          />
        ))}
      </Collapse>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const TaskSubtaskTree: React.FC<TaskSubtaskTreeProps> = ({
  parentTask,
  allTasks,
  onTaskClick,
  onAddSubtask,
  isLoading = false,
  maxDepth = 5,
}) => {
  // Get all subtasks (direct children)
  const directSubtasks = allTasks.filter(
    (t) => t.parentTaskId === parentTask.id
  );

  // Calculate overall progress
  const getAllDescendants = (taskId: string): Task[] => {
    const children = allTasks.filter((t) => t.parentTaskId === taskId);
    return children.reduce(
      (acc, child) => [...acc, child, ...getAllDescendants(child.id)],
      [] as Task[]
    );
  };

  const allDescendants = getAllDescendants(parentTask.id);
  const completedDescendants = allDescendants.filter(
    (t) => t.status === TaskStatus.COMPLETED
  ).length;
  const totalDescendants = allDescendants.length;
  const overallProgressRaw =
    totalDescendants > 0
      ? Math.round((completedDescendants / totalDescendants) * 100)
      : 0;
  const overallProgress =
    parentTask.status === TaskStatus.COMPLETED ? 100 : overallProgressRaw;

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={`${60 + i * 10}%`} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GitBranch size={18} style={{ color: '#64748b' }} />
          <Typography variant="subtitle2" fontWeight={600}>
            Subtasks
          </Typography>
          {totalDescendants > 0 && (
            <Chip
              size="small"
              label={`${completedDescendants}/${totalDescendants}`}
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 500,
              }}
            />
          )}
        </Box>
        <Button
          size="small"
          startIcon={<Plus size={14} />}
          onClick={() => onAddSubtask?.(parentTask)}
          sx={{ textTransform: 'none' }}
        >
          Add Subtask
        </Button>
      </Box>

      {/* Overall Progress */}
      {totalDescendants > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Overall Progress
            </Typography>
            <Typography variant="caption" fontWeight={500}>
              {formatPercent(overallProgress)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={overallProgress}
            sx={{
              height: 6,
              borderRadius: 1,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor: overallProgress === 100 ? 'success.main' : 'primary.main',
                borderRadius: 1,
              },
            }}
          />
        </Box>
      )}

      {/* Tree Content */}
      {directSubtasks.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'grey.300',
          }}
        >
          <GitBranch
            size={32}
            style={{ color: '#9ca3af', marginBottom: 8 }}
          />
          <Typography variant="body2" color="text.secondary">
            No subtasks yet
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Break this task down into smaller, manageable pieces
          </Typography>
          <Button
            size="small"
            startIcon={<Plus size={14} />}
            onClick={() => onAddSubtask?.(parentTask)}
            sx={{ mt: 1.5, textTransform: 'none' }}
          >
            Add First Subtask
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: 1,
            borderColor: 'grey.200',
            overflow: 'hidden',
          }}
        >
          {directSubtasks.map((subtask) => (
            <SubtaskNode
              key={subtask.id}
              task={subtask}
              allTasks={allTasks}
              level={0}
              onTaskClick={onTaskClick}
              onAddSubtask={onAddSubtask}
              maxDepth={maxDepth}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TaskSubtaskTree;
